import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import {
  WhatsappChatListViewModel,
  WhatsappMessageThreadViewModel,
  WhatsappMessageViewModel,
} from 'edu-application/dist/whatsapp-messages/base/whatsapp-chat.viewmodel';
import { environment } from 'src/environments/environment';
import { OrganizationService } from '../shared/organization.service';

export interface GetWhatsappChatsOptions {
  orgWhatsappId?: string;
  page?: number;
  pageSize?: number;
}

export interface GetWhatsappMessagesOptions {
  orgWhatsappId?: string;
  beforeMessageAt?: Date;
  pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class WhatsappService {
  private readonly mediaBlobCache = new Map<string, Observable<Blob>>();

  constructor(
    private http: HttpClient,
    private organizationService: OrganizationService,
  ) {}

  private get baseUrl(): string {
    const organizationId = this.organizationService.getOrganizationId();
    return `${environment.backend_url}/organizations/${organizationId}/whatsapp-chats`;
  }

  isInboxAvailable(): Observable<boolean> {
    return this.http
      .get<{ available: boolean }>(`${this.baseUrl}/access`)
      .pipe(
        map((response) => response.available),
        catchError(() => of(false)),
      );
  }

  getChats(options: GetWhatsappChatsOptions = {}): Observable<WhatsappChatListViewModel> {
    let params = new HttpParams();
    if (options.orgWhatsappId) {
      params = params.set('orgWhatsappId', options.orgWhatsappId);
    }
    if (options.page) {
      params = params.set('page', String(options.page));
    }
    if (options.pageSize) {
      params = params.set('pageSize', String(options.pageSize));
    }
    return this.http.get<WhatsappChatListViewModel>(this.baseUrl, { params });
  }

  getMessages(
    contactPhoneNumber: string,
    options: GetWhatsappMessagesOptions = {},
  ): Observable<WhatsappMessageThreadViewModel> {
    let params = new HttpParams();
    if (options.orgWhatsappId) {
      params = params.set('orgWhatsappId', options.orgWhatsappId);
    }
    if (options.beforeMessageAt) {
      params = params.set(
        'beforeMessageAt',
        options.beforeMessageAt.toISOString(),
      );
    }
    if (options.pageSize) {
      params = params.set('pageSize', String(options.pageSize));
    }
    return this.http.get<WhatsappMessageThreadViewModel>(
      `${this.baseUrl}/${encodeURIComponent(contactPhoneNumber)}/messages`,
      { params },
    );
  }

  sendReply(
    contactPhoneNumber: string,
    text: string,
    orgWhatsappId?: string,
    replyToMessageId?: string,
  ): Observable<WhatsappMessageViewModel> {
    return this.http.post<WhatsappMessageViewModel>(
      `${this.baseUrl}/${encodeURIComponent(contactPhoneNumber)}/reply`,
      { text, orgWhatsappId, replyToMessageId },
    );
  }

  sendMediaReply(
    contactPhoneNumber: string,
    file: File,
    orgWhatsappId?: string,
    caption?: string,
    replyToMessageId?: string,
  ): Observable<WhatsappMessageViewModel> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (caption?.trim()) {
      formData.append('caption', caption.trim());
    }
    if (orgWhatsappId) {
      formData.append('orgWhatsappId', orgWhatsappId);
    }
    if (replyToMessageId) {
      formData.append('replyToMessageId', replyToMessageId);
    }
    return this.http.post<WhatsappMessageViewModel>(
      `${this.baseUrl}/${encodeURIComponent(contactPhoneNumber)}/reply`,
      formData,
    );
  }

  markChatRead(
    contactPhoneNumber: string,
    orgWhatsappId: string,
  ): Observable<{ lastReadAt: Date }> {
    return this.http.post<{ lastReadAt: Date }>(
      `${this.baseUrl}/${encodeURIComponent(contactPhoneNumber)}/read?orgWhatsappId=${encodeURIComponent(orgWhatsappId)}`,
      {},
    );
  }

  /**
   * Media sits behind the authenticated media-proxy endpoint, so it is
   * fetched as a blob (interceptors attach auth headers) and rendered via
   * an object URL. Responses are cached for the browser session.
   */
  getMediaBlob(mediaId: string, orgWhatsappId: string): Observable<Blob> {
    const cacheKey = `${orgWhatsappId}::${mediaId}`;
    const cached = this.mediaBlobCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const request$ = this.http
      .get(
        `${this.baseUrl}/media/${encodeURIComponent(mediaId)}?orgWhatsappId=${encodeURIComponent(orgWhatsappId)}`,
        { responseType: 'blob' },
      )
      .pipe(shareReplay(1));

    this.mediaBlobCache.set(cacheKey, request$);
    return request$;
  }
}
